import { buildAppUrl, buildLoginUrl, readApp } from '../shared/utils/appUrl';
import { useState, useCallback, useEffect, useRef } from 'react';
import { apiCall } from '../utils/api';
import { AdminAppState, SessionInfo } from '../shared/types/system';

export function useSession(
	setState: React.Dispatch<React.SetStateAction<AdminAppState | null>>,
	session: SessionInfo | undefined,
	lastActivityRef: React.MutableRefObject<number>
): { isSessionNearExpiry: boolean; renew: () => Promise<void> } {
	const [isSessionNearExpiry, setIsSessionNearExpiry] = useState<boolean>(false);
	const isRenewingRef = useRef<boolean>(false);

	const renew = useCallback(async () => {
		try {
			const data = await apiCall<any>('renewSession');
			setState(current => {
				if (!current) return current;
				return { ...current, session: data };
			});
			setIsSessionNearExpiry(false);
		} catch (err) {
			console.error('Failed to renew session:', err);
			setIsSessionNearExpiry(true);
		}
	}, [setState]);

	useEffect(() => {
		if (!session?.expires) return;

		const checkExpiration = () => {
			const now = Date.now();
			const timeLeft = session.expires - now;

			if (timeLeft <= 0) {
				if (import.meta.env.DEV) {
					console.warn('[AUTH] Session expired. Redirect suppressed in dev mode.');
					return;
				}

				const programId = session.programId;
				const app = readApp(new URLSearchParams(window.location.search)) ?? 'admin';

				console.warn('[AUTH] Session expired. Redirecting to login.');

				// ADR-129: only the expired scope's token; the Admin Portal and Ops Hub sessions are independent.
				if (app === 'ops') {
					localStorage.removeItem('volunteer_session_token');
					sessionStorage.removeItem('volunteer_session_token');
				} else {
					sessionStorage.removeItem('session_token');
				}

				if (app === 'ops') {
					window.location.href = buildAppUrl('', { app: 'ops', programId }, { expired: 'true' });
				} else {
					window.location.href = buildLoginUrl('', { app, programId }, { expired: 'true' });
				}
			} else if (timeLeft < 10 * 60 * 1000) {
				// DL-277: widened from 5 to 15 min so a normal mid-task gap (stepping away to
				// think, a quick call) still silently auto-renews instead of surfacing the
				// near-expiry banner.
				const recentlyActive = Date.now() - lastActivityRef.current < 15 * 60 * 1000;
				if (recentlyActive && !isRenewingRef.current) {
					isRenewingRef.current = true;
					renew().finally(() => { isRenewingRef.current = false; });
					return;
				} else if (timeLeft < 5 * 60 * 1000) {
					setIsSessionNearExpiry(true);
				}
			} else {
				setIsSessionNearExpiry(false);
			}
		};

		checkExpiration();
		const interval = setInterval(checkExpiration, 60000);

		const timeLeft = session.expires - Date.now();
		let timeout: ReturnType<typeof setTimeout>;
		if (timeLeft > 0 && timeLeft < 2147483647) {
			timeout = setTimeout(checkExpiration, timeLeft + 1000);
		}

		return () => {
			clearInterval(interval);
			if (timeout) clearTimeout(timeout);
		};
	}, [session?.expires, session?.programId, renew, lastActivityRef]);

	return { isSessionNearExpiry, renew };
}
